import { MouseEvent, useCallback, useEffect, useRef, useState } from 'react';

const CLOSE_DELAY_MS = 150;

export interface UseSubmenuHoverReturn {
  anchorEl: HTMLElement | null;
  open: boolean;
  /** Attach to the trigger's onMouseEnter and onClick. */
  openSubmenu: (event: MouseEvent<HTMLElement>) => void;
  /** Attach to onClose and call after selecting an option. */
  closeSubmenu: () => void;
  /** Attach to the trigger's and the submenu list's onMouseLeave. */
  scheduleClose: () => void;
  /** Attach to the submenu list's onMouseEnter to keep it open. */
  cancelClose: () => void;
}

/**
 * Hover/click state for a nested context-menu submenu. Opening on hover is
 * convenient, but the nested MUI Menu portals outside the trigger, so it needs
 * an explicit close-on-mouse-leave (with a short grace period to let the pointer
 * travel from the trigger into the submenu) — otherwise it stays stuck open.
 */
export default function useSubmenuHover(): UseSubmenuHoverReturn {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimer = useCallback(() => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  }, []);

  const openSubmenu = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      clearTimer();
      setAnchorEl(event.currentTarget);
    },
    [clearTimer],
  );

  const closeSubmenu = useCallback(() => {
    clearTimer();
    setAnchorEl(null);
  }, [clearTimer]);

  const scheduleClose = useCallback(() => {
    clearTimer();
    closeTimer.current = setTimeout(() => setAnchorEl(null), CLOSE_DELAY_MS);
  }, [clearTimer]);

  useEffect(() => clearTimer, [clearTimer]);

  return {
    anchorEl,
    open: Boolean(anchorEl),
    openSubmenu,
    closeSubmenu,
    scheduleClose,
    cancelClose: clearTimer,
  };
}
